import type { ReactNode } from 'react';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import { ProfileContainer } from './ProfileContainer';

export interface ProfileHeroStat {
  label: string;
  /** An empty value (`undefined`, `null`, `''`) keeps its column and shows a dash (FR-164). */
  value?: ReactNode;
}

export interface ProfileHeroProps {
  avatar: ReactNode;
  name: string;
  /** A small line above the name (an investor's firm name over its fund name, FR-187). */
  eyebrow?: string;
  /** Status pills beside the name (`ProfileHeroBadge`). */
  badges?: ReactNode;
  description?: string;
  /** One line of facts and links under the description. */
  meta?: ReactNode;
  /** Page actions (Export as PDF). */
  actions?: ReactNode;
  stats?: ProfileHeroStat[];
}

// Literal class names: Tailwind only generates classes it can find verbatim.
const STAT_COLUMNS: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
};

function isEmpty(value: ReactNode): boolean {
  return value === undefined || value === null || value === '';
}

function HeroStats({ stats }: { stats: ProfileHeroStat[] }) {
  if (stats.length === 0) return null;
  return (
    <dl
      className={`mt-8 grid grid-cols-2 border-t border-white/10 ${STAT_COLUMNS[stats.length] ?? 'md:grid-cols-5'}`}
    >
      {stats.map((stat, index) => (
        <div
          key={stat.label}
          className={`min-w-0 py-5 ${index === 0 ? '' : 'border-white/10 md:border-l md:pl-6'}`}
        >
          <dt className="text-xs text-slate">{stat.label}</dt>
          <dd className="text-p2 mt-0.5 font-semibold break-words">
            {isEmpty(stat.value) ? L.EMPTY_STAT : stat.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

const BADGE_TONE = {
  green: 'bg-green/18 text-green-on-navy',
  muted: 'bg-white/10 text-on-navy-muted',
  featured: 'bg-yellow/30 text-on-yellow-muted',
} as const;

/** A status pill beside a profile name: `green`/`muted` on the navy header, `featured` on white. */
export function ProfileHeroBadge({
  tone,
  children,
}: {
  tone: 'green' | 'muted' | 'featured';
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${BADGE_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * The navy profile page header (FR-164, client-approved Option B): full-bleed
 * under the navbar, identity and page actions on top, a strip of key numbers
 * along its bottom edge. Used by the startup and investor profiles and the
 * shared `/p/` page; the partner profile keeps Option D's cover-photo card.
 */
export function ProfileHero({
  avatar,
  name,
  eyebrow,
  badges,
  description,
  meta,
  actions,
  stats = [],
}: ProfileHeroProps) {
  return (
    <header className="bg-navy text-white">
      <ProfileContainer className="pt-10">
        <div className="flex flex-wrap items-start gap-6 md:gap-7">
          <div className="shrink-0">{avatar}</div>
          <div className="min-w-0 flex-[1_1_26rem]">
            {eyebrow && <p className="text-p6 text-on-navy-muted mb-1 font-medium">{eyebrow}</p>}
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-h2 font-sans font-semibold tracking-normal">{name}</h1>
              {badges}
            </div>
            {description && (
              <p className="text-p5 text-on-navy-muted mt-1.5 max-w-[42.5rem] leading-6">
                {description}
              </p>
            )}
            {meta && (
              <div className="mt-3.5 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate">{meta}</div>
            )}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
        </div>
        <HeroStats stats={stats} />
      </ProfileContainer>
    </header>
  );
}
